import { BcAnalytics } from 'common/analytics/analytics.service';
import { OperatorStatusService } from 'shared/services/operator-status/operator-status.service';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { WorkViewTaskTabEvents } from 'common/common/constants/analytics-enums';
import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { AccordionPanelStates } from 'components/accordion/model/accordion-panel-states';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { bcStates } from 'common/common/states';
import { BcConfigService } from '../../common/config/provider';
import { Store } from '@ngrx/store';
import { selectAllActiveConversationCount } from 'common/store/genesys-cloud/conversations/common/selectors/conversation.selectors';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Component({
  selector: 'app-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AccordionComponent implements OnInit {
  public activePanelState: AccordionPanelStates = AccordionPanelStates.ACTIVE_CONVERSATIONS;
  public readonly accordionPanelStates = AccordionPanelStates;
  public readonly isBoldEnabled = this._commonAuth.isBoldEnabled();
  public readonly isHybrid = this._commonAuth.isHybrid();
  public readonly activeGenesysConversationCount$ = this.store.select(selectAllActiveConversationCount);

  private _hasQueueViewFeature: boolean = false;
  private _hasAgentStatsFeature: boolean = false;

  constructor(private _workItemListService: WorkItemListService,
              private _stateService: StateServiceWrapper,
              private _bcAnalytics: BcAnalytics,
              private _operatorStatusService: OperatorStatusService,
              private _settingsService: BcSettingsService,
              private _userPermissionService: UserPermissionService,
              private _bcConfig: BcConfigService,
              private _changeDetector: ChangeDetectorRef,
              private readonly store: Store,
              private _commonAuth: CommonAuthService) {
  }

  async ngOnInit(): Promise<void> {
    if (await this._commonAuth.isBoldEnabled()) {
      await this._getFeatureValues();
    }
  }

  get isQueueViewEnabled(): boolean {
    return (this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_GRID_VIEW_ACTIVE)
      || this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_GRID_VIEW_INACTIVE)
      || this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_GRID_VIEW_MY_ACTIVE)
      || this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_GRID_VIEW_MY_INACTIVE))
      && this._hasQueueViewFeature;
  }

  get isAgentStatsEnabled(): boolean {
    return this._hasAgentStatsFeature && this._bcConfig.current.features.agentStats
      && this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_AGENT_STATS);
  }

  public isActiveConversationsPanelActive(): boolean {
    return this.activePanelState === AccordionPanelStates.ACTIVE_CONVERSATIONS;
  }

  public isSearchConversationsPanelActive(): boolean {
    return this.activePanelState === AccordionPanelStates.SEARCH_CONVERSATIONS;
  }

  public panelOpen(panelState: AccordionPanelStates): void {
    this.activePanelState = panelState;

    switch (panelState) {
      case AccordionPanelStates.ACTIVE_CONVERSATIONS:
        this._bcAnalytics.eventTrack(WorkViewTaskTabEvents.ACTIVE_TAB_SELECTED);
        break;
      case AccordionPanelStates.SEARCH_CONVERSATIONS:
        this._bcAnalytics.eventTrack(WorkViewTaskTabEvents.CLOSED_TAB_SELECTED);
        break;
      default:
        break;
    }
  }

  public isRedDotNotificationDisabled(): boolean {
    return this._workItemListService.getRedDotNotificationForAccordionDisabledValue();
  }

  public getActiveWorkitemCount(): number {
    return this._workItemListService.getActiveWorkitemCount();
  }

  public getUnansweredMessageCount(): number {
    return this._workItemListService.getUnansweredMessageCount();
  }

  public getSecondsRemainingDisplay(): string {
    return this._workItemListService.getSecondsRemainingDisplay();
  }

  public goToQueuePage(): void {
    const chatStatus = this._operatorStatusService.getStatusKey(bcEnums.services.CHATS);
    this._bcAnalytics.eventTrack('QueueViewOpened', { chatStatus: chatStatus, activeChatsCount: this._workItemListService.getChatWorkitemCount() });
    this._stateService.go(bcStates.queueView.url);
  }

  private _getFeatureValues(): Promise<void> {
    return this._settingsService.getSettings().then(settings => {
      this._hasQueueViewFeature = this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_QUEUE_VIEW);
      this._hasAgentStatsFeature = this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_AGENT_STATS);
      this._changeDetector.detectChanges();
    });
  }
}
